import { useCallback, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useMyCareerVacancyFeedQuery, useMyCareerFilterDictionariesQuery } from 'core/graphql';
import { authState } from 'core/state';
import { useCareerCapabilitySkip } from 'core/hooks/CareerJourney/useCareerCapabilitySkip';
import { isCareerViewer } from './isCareerViewer';

import { IFeedVacancy, transformFeedVacancy } from './transform';

export interface ICareerFeedFilters {
  directionId?: string;
  skillIds?: string[];
  cityId?: string;
  workMode?: string;
  seniority?: string;
  employmentType?: string;
  salaryFrom?: number;
  freshness?: string;
  search?: string;
  savedOnly?: boolean;
  sort?: string;
  /** ТЗ 0071, п. 8.4: 'GOAL' | 'PROFILE'; пусто — сервер решает сам (цель, если выбрана). */
  matchMode?: string;
  /**
   * Не спрашивать сервер, пока фильтр ещё не собран (ТЗ 0070).
   *
   * 🔴 Нужен переходу «паспорт навыка → лента»: в адресе стоит СЛАГ навыка, а
   * запросу нужен его id, и он приезжает отдельным ответом. Без паузы первый
   * кадр уходит на сервер БЕЗ фильтра — человек видит всю ленту, которая через
   * мгновение схлопывается до трёх строк, и читает это как поломку.
   */
  paused?: boolean;
}

const PAGE_SIZE = 20;

/** Blanks collapse to `undefined` so the server drops the arg instead of matching on ''. */
const clean = (value?: string): string | undefined => value?.trim() || undefined;

/**
 * The ranked, paged vacancy feed (spec 0035).
 *
 * Replaces `useCareerVacancies` for the list. Three things it does that the
 * old hook could not:
 *
 * * **Ranks against the student's own profile** — and reports `relevanceApplied`
 *   so the page can stop claiming a match when there is nothing to match on;
 * * **Pages with a keyset cursor**, which survives the nightly agent
 *   publishing into the top of the order while someone is scrolling;
 * * **Backs its filters with real dictionaries** instead of scraping direction
 *   options out of the feed's own rows into a ref that never reset.
 *
 * `fetchMore` appends: the merge lives in the Apollo type policy so a
 * page-2 fetch does not replace page 1.
 */
export const useCareerVacancyFeed = (filters: ICareerFeedFilters) => {
  const { active_role, isDemoUser } = useReactiveVar(authState);
  // ТЗ 0069: не-участник больше не выбрасывается со страницы — он остаётся на
  // ней и читает объяснение. Без этого пропуска сервер отбил бы ленту, и
  // экран показал бы «ошибка загрузки» вместо «раздел откроется участникам».
  //
  // Справочники фильтров НИЖЕ пропуска не получают: они открыты любому
  // студенту и на сервере, и заглушить их значило бы показать закрытому
  // разделу пустые фильтры вместо настоящих.
  const lockedSkip = useCareerCapabilitySkip('vacancies');
  // Every career resolver rejects a non-Student LMS token, so running this for
  // anyone else would only produce a guaranteed PermissionDenied.
  const skip =
    !isCareerViewer(active_role) || Boolean(isDemoUser) || lockedSkip || Boolean(filters.paused);

  const variables = useMemo(
    () => ({
      directionId: clean(filters.directionId),
      skillIds: filters.skillIds?.length ? filters.skillIds : undefined,
      cityId: clean(filters.cityId),
      workMode: clean(filters.workMode),
      seniority: clean(filters.seniority),
      employmentType: clean(filters.employmentType),
      salaryFrom: filters.salaryFrom || undefined,
      freshness: clean(filters.freshness),
      search: clean(filters.search),
      // `undefined`, not `false`: the server then never receives the argument
      // and the cache key matches the plain feed, so toggling the switch back
      // and forth costs no extra request.
      savedOnly: filters.savedOnly ? true : undefined,
      sort: clean(filters.sort),
      matchMode: clean(filters.matchMode),
      first: PAGE_SIZE,
    }),
    [filters],
  );

  const { data, loading, error, fetchMore, refetch } = useMyCareerVacancyFeedQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip,
  });

  const connection = data?.careerVacancyFeed;

  const vacancies: IFeedVacancy[] = useMemo(
    () => (connection?.edges ?? []).map((edge) => transformFeedVacancy(edge.node)),
    [connection],
  );

  const loadMore = useCallback(() => {
    const cursor = connection?.pageInfo?.endCursor;
    if (!cursor || !connection?.pageInfo?.hasNextPage) return;
    fetchMore({ variables: { ...variables, after: cursor } });
  }, [connection, fetchMore, variables]);

  return {
    vacancies,
    totalCount: connection?.totalCount ?? 0,
    totalCountCapped: Boolean(connection?.totalCountCapped),
    // 🔴 The page must read this before saying anything about matching.
    relevanceApplied: Boolean(connection?.relevanceApplied),
    // ТЗ 0071, п. 8.3: ноль по фильтрам и пустая база — разные экраны.
    totalAvailable: connection?.totalAvailable ?? 0,
    matchContext: connection?.matchContext
      ? {
          matchMode: connection.matchContext.matchMode,
          trackCode: connection.matchContext.trackCode,
          trackTitle: connection.matchContext.trackTitle,
          fallbackReason: connection.matchContext.fallbackReason,
        }
      : null,
    hasMore: Boolean(connection?.pageInfo?.hasNextPage),
    loading,
    error: error?.message || '',
    loadMore,
    /** Повтор после ошибки загрузки (0072): «ошибка» ≠ «вакансий нет». */
    refetch,
  };
};

/** Direction and city option lists for the filter panel. */
export const useCareerFilterDictionaries = () => {
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const skip = !isCareerViewer(active_role) || Boolean(isDemoUser);

  // `cache-first`: the panel needs its options before any result arrives, and
  // a curated dictionary does not change during a session.
  const { data } = useMyCareerFilterDictionariesQuery({
    fetchPolicy: 'cache-first',
    skip,
  });

  const directionOptions = useMemo(
    () =>
      (data?.careerDirectionsLms ?? []).map((row) => ({
        value: row.id,
        label: row.title,
      })),
    [data],
  );

  const cityOptions = useMemo(
    () =>
      (data?.careerCities ?? []).map((row) => ({
        value: row.id,
        label: row.name,
      })),
    [data],
  );

  return { directionOptions, cityOptions };
};
